import { type ReactElement, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { Button } from '@autop/ui/src/button';

import { useCompleteOnboarding } from '@/features/complete-onboarding';
import {
  EMPTY_PROFILE,
  isNicknameReady,
  joinTimeSink,
  type MemberProfile,
  NicknameField,
  type NicknameValue,
  useSession,
} from '@/entities/session';
import { OTHER } from '@/shared/config';
import { toInternalPath } from '@/shared/lib/internal-path';

import { StepAutomationLevel, StepIndicator, StepTimeSink } from './onboarding-steps';

/** 서버가 준 문구가 없을 때 보여줄 말. 영어 원문(`Failed to fetch`)을 그대로 띄우지 않는다. */
const FALLBACK_MESSAGE = '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.';

const toMessage = (error: unknown): string =>
  error instanceof Error && error.message.length > 0 ? error.message : FALLBACK_MESSAGE;

/**
 * **한 화면에 질문 하나.** 단계 수가 아니라 한 화면의 밀도가 이탈을 만든다.
 * 항목을 더 받고 싶다고 한 화면에 두 개를 쌓지 않는다 — 그러면 둘 다 안 채워진다.
 */
const STEPS = ['닉네임', '가장 지겨운 반복 업무', '자동화 경험'] as const;

const TOTAL_STEPS = STEPS.length;

type Draft = MemberProfile;

function nicknameOf(draft: Draft): NicknameValue {
  return { jobRole: draft.jobRole, jobRoleTag: draft.jobRoleTag, handle: draft.name };
}

/**
 * 각 단계를 넘어갈 수 있는 조건.
 *
 * 세 질문 모두 **필수**다. 세 값이 다 있어야 소분방 배정과 자료 추천이 돌아가는데,
 * 하나라도 비면 그 회원은 어느 축에도 못 들어가 결국 아무것도 못 받는다.
 * 대신 질문을 셋으로 줄이고 전부 고르기만 하면 끝나게 해서 부담을 낮춘다.
 */
function isStepAnswered(step: number, draft: Draft): boolean {
  if (step === 1) return isNicknameReady(nicknameOf(draft));
  if (step === 2)
    return (
      draft.topTimeSink !== '' && (draft.topTimeSink !== OTHER || !!draft.topTimeSinkOther.trim())
    );
  return draft.automationLevel !== '';
}

/**
 * 가입 온보딩 — 14. 1차 오픈 범위 §6.2.
 *
 * **가입 뒤에 둔다.** 게이트 앞에 놓인 필드는 전환율을 직접 깎지만, 여기 필드는 이미 가입한
 * 사람이 보므로 회원은 이미 확보돼 있다(13 §6.2). 그래서 여기서는 필수로 받을 수 있다.
 *
 * 소셜로 들어온 사람도 여기를 거친다 — 제공자가 넘겨주는 건 계정 정보뿐이라
 * 이 화면을 건너뛰면 세그먼트가 통째로 빈다.
 *
 * 「건너뛰기」가 없다. 세 값이 다 있어야 배정이 돌아가므로 빠져나갈 문을 두면 그 회원은
 * 아무 방에도 못 들어간다. 대신 **닉네임 → 업무 → 경험** 순으로, 뒤로 갈수록 가볍게 놓아
 * 앞에서 들인 품이 아까워 끝까지 가게 한다.
 */
export function OnboardingPage(): ReactElement {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // 주소창의 값이라 그대로 믿지 않는다 — `//evil.com` 은 브라우저가 다른 사이트로 읽는다.
  const next = toInternalPath(params.get('next'), '/library');

  const completeOnboarding = useCompleteOnboarding();

  const member = useSession((state) => state.member);

  const [step, setStep] = useState(1);
  /**
   * 입력 중인 값. 스토어를 **한 번만** 옮겨 담는다 — 저장이 스토어를 갱신해도
   * 입력 중인 값을 덮지 않는다.
   *
   * 빈 회원을 `{} as Draft` 로 때우지 않는다. 그 캐스트는 타입만 맞추고 값은
   * 전부 `undefined` 라, 첫 렌더의 `isStepAnswered` 가 `undefined.trim()` 으로
   * **화면을 통째로 죽인다.** 새로고침이면 스토어는 `member: null` 에서 시작하고
   * (session.store.ts — 브라우저에 세션을 남기지 않는다), 세션은 부팅 재발급의
   * 왕복이 끝나야 채워지므로 이 화면은 그 전에 먼저 그려진다.
   */
  const [draft, setDraft] = useState<Draft>(() => useSession.getState().member ?? EMPTY_PROFILE);
  const [error, setError] = useState<string | null>(null);

  /**
   * 이 화면의 값이 이미 정해졌는지. 정해졌으면 늦게 온 세션으로 덮지 않는다.
   *
   * 정해지는 경우는 둘이다 — 마운트 시점에 세션이 이미 있었거나, 사용자가 한 글자라도
   * 쳤거나. 후자를 빼먹으면 **타이핑하는 중에 부팅 재발급이 끝나는 순간 방금 친 글자가
   * 눈앞에서 사라진다.**
   */
  const [isDraftSettled, setIsDraftSettled] = useState(() => useSession.getState().member !== null);

  /**
   * 부팅 재발급이 늦게 끝났을 때 그 값을 **한 번** 받아 채운다.
   *
   * 없으면 이미 온보딩을 지난 사람이 새로고침으로 들어왔을 때 빈 칸에서 다시 시작한다.
   * `useEffect` 가 아니라 렌더 중에 정하는 이유는, 효과로 미루면 빈 화면이 한 번
   * 그려졌다가 값이 튀어 들어오기 때문이다.
   */
  if (!isDraftSettled && member !== null) {
    setIsDraftSettled(true);
    setDraft(member);
  }

  /**
   * 연타 방어. `isPending` 은 뮤테이션 구독을 거쳐 갱신되므로, 클릭과 버튼이 실제로
   * 잠기는 사이에 틈이 있다. 그 틈으로 두 번째 제출이 들어가면 같은 답을 두 번 저장한다.
   */
  const isSubmitting = useRef(false);

  const heading = STEPS[step - 1] ?? STEPS[0];
  const isLast = step === TOTAL_STEPS;
  const canAdvance = isStepAnswered(step, draft);

  const patch = (partial: Partial<Draft>) => {
    // 사용자가 손을 댄 순간부터 이 값이 정본이다 — 늦게 온 세션에 밀리지 않는다.
    setIsDraftSettled(true);
    setDraft((prev) => ({ ...prev, ...partial }));
  };

  /**
   * 마지막 단계에서만 저장한다 — 단계마다 보내면 중간에 그만둔 사람이
   * 절반만 채워진 채로 「완료」로 표시된다.
   *
   * **저장에 성공한 뒤에만 다음 화면으로 보낸다.** 먼저 보내면 실패를 알릴 자리가 없고,
   * 사용자는 다 채웠다고 믿은 채 떠난다.
   */
  const finish = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setError(null);

    try {
      await completeOnboarding({
        name: draft.name.trim(),
        jobRole: draft.jobRole,
        jobRoleTag: draft.jobRoleTag,
        // 서버 컬럼은 하나다. 「기타」면 직접 적은 문구가 그 자리에 들어간다.
        topTimeTask: joinTimeSink(draft.topTimeSink, draft.topTimeSinkOther),
        automationLevel: draft.automationLevel,
      });
      void navigate(next, { replace: true });
    } catch (cause) {
      setError(toMessage(cause));
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <div className="u-container flex justify-center py-16">
      <div className="flex w-full max-w-[32rem] flex-col gap-7">
        <StepIndicator current={step} total={TOTAL_STEPS} />

        <h1 className="text-[1.375rem] font-bold tracking-[-0.04em]">{heading}</h1>

        {step === 1 ? (
          <NicknameField
            value={nicknameOf(draft)}
            onChange={(nickname) =>
              patch({
                jobRole: nickname.jobRole,
                jobRoleTag: nickname.jobRoleTag,
                name: nickname.handle,
              })
            }
            idPrefix="onboarding-nickname"
          />
        ) : null}
        {step === 2 ? (
          <StepTimeSink
            topTimeSink={draft.topTimeSink}
            topTimeSinkOther={draft.topTimeSinkOther}
            onChange={patch}
          />
        ) : null}
        {step === 3 ? (
          <StepAutomationLevel automationLevel={draft.automationLevel} onChange={patch} />
        ) : null}

        {error !== null ? (
          <p role="alert" className="text-[13px] text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2">
          {step > 1 ? (
            <Button variant="secondary" size="lg" onClick={() => setStep(step - 1)}>
              이전
            </Button>
          ) : null}
          <Button
            size="lg"
            className="flex-1"
            disabled={!canAdvance}
            onClick={() => (isLast ? void finish() : setStep(step + 1))}
          >
            {isLast ? '자료 받으러 가기' : '다음'}
          </Button>
        </div>
      </div>
    </div>
  );
}
